// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useMemo } from "react";
import { isTokenExpired } from "../components/utils/authHelpers";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const genAppLoginAuthContext = (token, userData) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));

    setToken(token);
    setIsAuthenticated(true);
    setUser(userData);
  };

  const genAppLogoutAuthContext = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    setIsAuthenticated(false);
    setToken(null);
    setUser(null);
  };

  const login = (token, userData) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setToken(token);
    setIsAuthenticated(true);
    setUser(userData);
    setLoading(false); // Immediately mark as loaded after login
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setIsAuthenticated(false);
    setToken(null);
    setUser(null);
  };

  /**
   * Update user data within the context and synchronize with localStorage.
   * @param {Object} updatedUserData - Partial user data to update.
   */
  const updateUser = (updatedUserData) => {
    if (user) {
      const newUser = { ...user, ...updatedUserData };
      setUser(newUser);
      localStorage.setItem("user", JSON.stringify(newUser));
    }
  };

  const getCurrentPSTTime = () => {
    return new Date(
      new Date().toLocaleString("en-US", { timeZone: "America/Los_Angeles" })
    );
  };

  const isGenAppOverrideActive = useMemo(() => {
    if (user?.admin_type_id && user?.admin_type_id <= 1) return true; // override for NLSF Admin and Super Admin
    if (!user?.access_override_until) return false;

    const overrideDate = new Date(user.access_override_until);
    const currentPST = getCurrentPSTTime();

    return overrideDate > currentPST;
  }, [user?.access_override_until, user?.admin_type_id]);

  const isDasAppOverrideActive = useMemo(() => {
    if (user?.admin_type_id && !user?.admin_type_id <= 1) return true; // override for NLSF Admin and Super Admin
    if (!user?.dasAccessOverrideUntil) return false;

    const dasOverrideDate = new Date(user.dasAccessOverrideUntil);
    const currentPST = getCurrentPSTTime();

    return dasOverrideDate > currentPST;
  }, [user?.dasAccessOverrideUntil, user?.admin_type_id]);

  useEffect(() => {
    // Synchronously check localStorage on mount
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        // Quick token expiry check
        if (!isTokenExpired(token)) {
          setToken(token);
          setIsAuthenticated(true);
          setUser(parsedUser);
        } else {
          // Clear expired token immediately
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        }
      } catch (error) {
        console.error("Failed to parse stored user:", error);
        localStorage.removeItem("user");
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const checkTokenExpiry = () => {
      const token = localStorage.getItem("token");
      if (token && isTokenExpired(token)) {
        logout();
      }
    };

    checkTokenExpiry();

    const interval = setInterval(checkTokenExpiry, 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        token,
        genAppLoginAuthContext,
        genAppLogoutAuthContext,
        login,
        logout,
        updateUser,
        loading,
        isGenAppOverrideActive,
        isDasAppOverrideActive,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
